import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch, useSelector } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import FilterClearIcon from '../../assets/icons/filter-clear-icon.svg?react';
import FilterIcon from '../../assets/icons/filter-icon.svg?react';
import SearchInput from '../../components/SearchInput';
import Select from '../../components/Select';
import {
  GetAllUsersResponse, OrganizationPaymentMethodEnum, User
} from '@EHSNavigator/backend';
import UserManagementRow from '../../components/UserManagementRow';
import { useNavigate } from 'react-router';
import { useDebounce, useLocalStorage } from '@uidotdev/usehooks';
import Pagination from '../../components/CustomPagination';
import ChangeUserRoleModal from '../../components/ChangeUserRoleModal';
import ModalComponent from '../../components/ModalComponent';
import DeleteUserModal from '../../components/DeleteUserModal';
import { useFormik } from 'formik';
import { getChangeUserRoleSchema, getDeleteUserSchema } from './schema';
import { toggleModal } from '../../reducers/modalToggle';
import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';
import moment from 'moment';
import { queryClient } from '../../main';
import { useMutation } from '@tanstack/react-query';
import NoItemsFound from '../../components/NoItemsFound';
import Loader from '../../components/Loader';
import { UserWithOrgContext } from '../../types/user';

interface UserManagementProps {
  otherClasses?: string;
  data: GetAllUsersResponse;
  params: {
    search: string;
    page: number;
    department: string;
    status: string;
    role: string;
    limit: number;
  };
  roles: {
    label: string;
    value: string;
  }[];
  departments: {
    label: string;
    value: string;
  }[];
  isLoading: boolean;
}
const INITIAL_STATUS = [
  {
    label: 'All',
    value: 'all',
  },
  {
    label: 'Active',
    value: 'active',
  },
  {
    label: 'Archived',
    value: 'archived',
  },
];

const UserManagement: React.FC<UserManagementProps> = ({
  otherClasses,
  data,
  departments,
  roles,
  params,
  isLoading,
}) => {
  const userManagementClasses = clsx(
    otherClasses,
    'flex flex-col w-full flex-1',
  );
  const { UserApi, UserSettingsApi } = useApiClients();
  const [editValues, setEditValues] = useState<User>();
  const [deleteValues, setDeleteValues] = useState<User>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const [userInfo, setUserInfo] = useLocalStorage<UserWithOrgContext | null>('userInfo', null);
  const organizationId = userInfo?.orgContext?.org.id || undefined;
  const organizationOwnerId = userInfo?.orgContext?.id || undefined;

  const [state, setState] = useState({
    page: params?.page,
    search: params?.search,
    status: params?.status,
    department: params?.department,
    role: params?.role,
    limit: params?.limit,
  });
  const debouncedSearch = useDebounce(state?.search, 600);
  const isParamsExist = Object.entries(state).some(([key, value]) => {
    if (key === 'status' && value === 'all') return false;
    if (key === 'page' && (value === 1 || value === '1')) return false;
    return value != null && value !== '';
  });
  const prevFiltersRef = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      search: debouncedSearch,
      status: state.status,
      department: state.department,
      role: state.role,
      limit: state.limit,
    };

    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }

    const paramsSearch = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value && !(key === 'status' && value === 'all')) {
        paramsSearch.set(key, value.toString());
      }
    });
    if (state.page !== 1) {
      paramsSearch.set('page', state.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/user-roles?${paramsSearch.toString()}`);
    }
    isFirstRun.current = false;
  }, [
    debouncedSearch,
    state.status,
    state.department,
    state.role,
    state.page,
    state.limit,
  ]);

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);
    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }
    navigate(`/user-roles?${params.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  const initialValues = {
    id: editValues?.id || '',
    employeeId: editValues?.employeeId || '',
    fullName: editValues?.fullName || '',
    departmentId: editValues?.departmentId || '',
    roleId: editValues?.role?.id || '',
    jobTitle: editValues?.jobTitle || '',
    contact: editValues?.contact || '',
    dob: editValues?.dob || '',
    dateOfHire: editValues?.dateOfHire || '',
  };

  const changeUserRoleHandler = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getChangeUserRoleSchema({ userInfo }),
    onSubmit: async (values: any) => {
      try {
        if (values.id === userInfo?.user?.id || values.id === userInfo?.orgContext?.id) {
          const res = await UserSettingsApi.userSettingsControllerUpdateProfile(
            {
              id: values?.id as string,
              updateUserSettingDto: {
                fullName: values?.fullName,
                contact: values?.contact,
                dob: values?.dob
                  ? moment(values?.dob).format('DD/MM/YYYY')
                  : null,
                ...(organizationId ? { organizationId } : {}),
                // dateOfHire: values?.dateOfHire
                //   ? moment(values?.dateOfHire).format('DD/MM/YYYY')
                //   : null,
              },
            },
          );
          setUserInfo((prev: any) => ({
            ...prev,
            user: {
              ...prev?.user,
              ...res?.data?.user,
              org: {
                ...prev?.user?.org,
              },
              role: {
                ...prev?.user?.role,
              },
            },
          }));
          queryClient.setQueryData(
            [
              'allUsers',
              params?.search,
              params?.page,
              params?.department,
              params?.role,
              params?.status,
              params?.limit,
              organizationId,
              organizationOwnerId,
            ],
            (oldData: any) => {
              const users = oldData.users.map((user: User) => {
                if (values?.id === user?.id) {
                  return { ...user, ...res?.data?.user };
                }
                return user;
              });
              return { ...oldData, users };
            },
          );
          dispatch(toggleModal(''));
          toast.success(res?.data?.message);
        } else {
          const response = await UserApi.userControllerUpdate({
            ...(organizationId ? { organizationId } : {}),
            id: values?.id,
            updateUserDTO: {
              ...(organizationId ? { organizationId } : {}),
              employeeId: values?.employeeId,
              fullName: values?.fullName,
              departmentId: values?.departmentId,
              roleId: values?.roleId,
              jobTitle: values?.jobTitle,
              contact: values?.contact,
              dob: values?.dob
                ? moment(values?.dob).format('DD/MM/YYYY')
                : null,
              dateOfHire: values?.dateOfHire
                ? moment(values?.dateOfHire).format('DD/MM/YYYY')
                : null,
            },
          });

          queryClient.setQueryData(
            [
              'allUsers',
              params?.search,
              params?.page,
              params?.department,
              params?.role,
              params?.status,
              params?.limit,
              organizationId,
              organizationOwnerId,
            ],
            (oldData: any) => {
              const users = oldData.users.map((user: User) => {
                if (values?.id === user?.id) {
                  return { ...user, ...response?.data?.user };
                }
                return user;
              });
              return { ...oldData, users };
            },
          );
          dispatch(toggleModal(''));
          toast.success(response?.data?.message);
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const deleteUserHandler = useFormik({
    initialValues: { name: '' },
    validationSchema: getDeleteUserSchema(deleteValues?.fullName || ''),
    onSubmit: async () => {
      try {
        const response: any = await UserApi.userControllerDeleteUser({
          id: String(deleteValues?.id),
          ...(organizationId ? { organizationId } : {}),
        });
        queryClient.setQueryData(
          [
            'allUsers',
            params?.search,
            params?.page,
            params?.department,
            params?.role,
            params?.status,
            params?.limit,
            organizationId,
            organizationOwnerId,
          ],
          (oldData: any) => {
            const users = oldData.users.filter(
              (user: User) => deleteValues?.id !== user?.id,
            );

            return {
              ...oldData,
              meta: {
                ...oldData.meta,
                total: oldData.meta.total - 1,
              },
              users,
            };
          },
        );
        deleteUserHandler?.resetForm();
        dispatch(toggleModal(''));
        setDeleteValues(undefined);
        toast.success(response?.data?.message);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const archiveUserHandler = useMutation({
    mutationFn: async (id: string) => {
      const response: any = await UserApi.userControllerToggleUserArchivedState(
        {
          id,
          ...(organizationId ? { organizationId } : {}),
        },
      );
      return response;
    },
    onSuccess: (data, id) => {
      queryClient.setQueryData(
        [
          'allUsers',
          params?.search,
          params?.page,
          params?.department,
          params?.role,
          params?.status,
          params?.limit,
          organizationId,
          organizationOwnerId,
        ],
        (oldData: any) => {
          if (state?.status === 'archived' || state?.status === 'active') {
            const users = oldData?.users?.filter(
              (user: User) => id !== user?.id,
            );
            return { ...oldData, users };
          } else {
            const users = oldData?.users?.map((user: User) => {
              if (user?.id === id) {
                return {
                  ...user,
                  isArchived: user?.isArchived ? false : true,
                };
              }
              return user;
            });
            return { ...oldData, users };
          }
        },
      );
      toast.success(data?.data?.message);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });
  const resendSetPasswordUserHandler = useMutation({
    mutationFn: async (id: string) => {
      const response: any = await UserApi.userControllerResendSetPasswordEmail({
        resendSetPasswordDto: {
          userId: id,
          ...(organizationId ? { organizationId } : {}),
        },
      });
      return response;
    },
    onSuccess: (data) => {
      toast.success(data?.data?.message);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const isResendSubmitting = resendSetPasswordUserHandler.isPending;
  const isArchiveSubmitting = archiveUserHandler.isPending;
  const isManualUser = userInfo?.user?.org?.paymentMethod === OrganizationPaymentMethodEnum.Manual;
  return (
    <div className={userManagementClasses} data-testid="user-management">
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">User Management</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            Manage all user roles from your organisation.
            <br />
            {isManualUser && (
              <span className="text-sm text-neutral-tones-text/80">
                License Allowed for{' '}
                <span className='text-brand-byzantine'>
                  {userInfo?.currentSubscription.max_count || 0} users.
                </span>
              </span>
            )}
          </p>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
        Manage all user roles from your organisation.
        <br />
        {isManualUser && (
          <span className="text-sm text-neutral-tones-text/80">
            License Allowed for{' '}
            <span className='text-brand-byzantine'>
              {userInfo?.currentSubscription.max_count || 0} users.
            </span>
          </span>
        )}
      </p>
      <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
      <SearchInput
        name="search"
        onChange={(e) =>
          setState((prev) => ({
            ...prev,
            search: e?.target?.value,
          }))
        }
        placeholder="Enter name, employee ID or department name..."
        value={state?.search}
        disabled={false}
        errorMessage=""
        otherClasses={{ container: 'w-full lg:mt-12 ' }}
      />

      <div className="global-scrollbar-style min-h-406 flex-1 overflow-auto px-0.5">
        <div className="flex w-full min-w-6xl flex-col">
          <div className="border-b-neutral-tones-text/50 relative mt-6 mb-12 flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-center lg:gap-9">
            <button
              onClick={() => {
                if (
                  state?.search !== '' ||
                  state?.department !== '' ||
                  state?.role !== '' ||
                  state?.status !== 'all' ||
                  state?.limit !== 10
                ) {
                  setState((prev) => ({
                    ...prev,
                    search: '',
                    department: '',
                    role: '',
                    status: 'all',
                    limit: 10,
                  }));
                }
              }}
              className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex cursor-pointer items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg"
            >
              {state?.role !== '' ||
                state?.department !== '' ||
                state?.status !== 'all' ||
                state?.search !== '' ||
                state?.limit !== 10 ? (
                <FilterClearIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-9 lg:w-9" />
              ) : (
                <FilterIcon className="h-6 w-6 lg:h-9 lg:w-9" />
              )}
              Filters
            </button>
            <div className="flex flex-1 items-center gap-6">
              <Select
                filterSelect
                list={departments}
                value={state?.department}
                placeholder="Departments"
                otherClasses={{ container: 'w-full max-w-48' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    department: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={roles}
                value={state?.role}
                placeholder="Role"
                otherClasses={{ container: 'w-full max-w-36' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    role: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={INITIAL_STATUS}
                value={state?.status}
                placeholder="Status"
                otherClasses={{ container: 'w-full max-w-36' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    status: e,
                  }))
                }
                errorMessage=""
              />
            </div>
          </div>
        </div>
        {isLoading ? (
          <Loader
            variant="compact"
            otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
          />
        ) : (
          <div className="w-full min-w-6xl">
            <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
              <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Sr.
              </h6>
              <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Employee ID
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Name
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 font-bold lg:text-lg lg:leading-5">
                Email
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Departments
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Role
              </h6>
            </div>
            {data?.users?.length > 0 ? (
              <div>
                {data?.users?.map((node, index: number) => (
                  <UserManagementRow
                    key={node.id || `user-${index}`}
                    user={node}
                    index={(state.page - 1) * state.limit + index + 1}
                    lastTwoIndex={data?.users?.slice(-2)}
                    setEditValues={setEditValues}
                    setDeleteValues={setDeleteValues}
                    handleArchive={(e) => archiveUserHandler?.mutate(e)}
                    isArchiveSubmitting={isArchiveSubmitting}
                    handleResendSetPassword={(e) =>
                      resendSetPasswordUserHandler?.mutate(e)
                    }
                    isResendSubmitting={isResendSubmitting}
                  />
                ))}
              </div>
            ) : (
              <NoItemsFound
                message={
                  isParamsExist
                    ? 'No items match your filters or search. Try using different keywords maybe.'
                    : "User doesn't exist"
                }
              />
            )}
          </div>
        )}
      </div>
      {data?.users?.length > 0 && (
        <Pagination
          currentPage={state.page}
          totalPages={data?.meta?.totalPages}
          totalItems={data?.meta?.total}
          limit={data?.meta?.limit}
          showingItems={data?.users?.length}
          onPageChange={handlePageChange}
          onLimitChange={(e) =>
            setState((prev) => ({ ...prev, limit: e, page: 1 }))
          }
          otherClasses=""
        />
      )}
      {(modalName === 'change-user-role' || modalName === 'delete-user') && (
        <ModalComponent
          otherClasses="px-4 sm:px-0 "
          additionalClass={clsx(
            'w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6 ',
            modalName === 'change-user-role' && 'overflow-visible',
          )}
          customClose={() => {
            dispatch(toggleModal(''));
            setEditValues(undefined);
            setDeleteValues(undefined);
          }}
        >
          {modalName === 'change-user-role' && (
            <ChangeUserRoleModal
              roles={roles}
              departments={departments}
              initialValues={initialValues}
              formHandler={changeUserRoleHandler}
              editValues={editValues}
              handleClose={() => {
                dispatch(toggleModal(''));
                setEditValues(undefined);
              }}
            />
          )}
          {modalName === 'delete-user' && (
            <DeleteUserModal
              formHandler={deleteUserHandler}
              handleClose={() => dispatch(toggleModal(''))}
              title={deleteValues?.fullName || ''}
            />
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default UserManagement;
